import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { SessionService as SessionServiceLegacy } from '@services/session.service';
import { SessionService } from '@arkius/shared-services';
import { Storage } from '@constants/storage';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { ApiService } from '@services/api.service';
import { Router } from '@angular/router';

@Injectable()
export class AdminAuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    private router: Router,
    private sessionLegacy: SessionServiceLegacy,
    private session: SessionService,
    private api: ApiService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // if (this.router.url.startsWith('/public/')) {
    //   return next.handle(request);
    // }

    const accessToken = this.session.get(Storage.SESSION_TOKEN);
    if (accessToken) {
      request = this.addHeaders(request, accessToken);
      return next.handle(request).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(request, next);
          } else {
            return throwError(() => error);
          }
        }),
      );
    } else {
      request = this.addHeaders(request, null);
      return next.handle(request).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.redirectToLogin();
          } else {
            return throwError(() => error);
          }
        }),
      );
    }
  }

  private addHeaders(request: any, data: any): any {
    if (data) {
      if (request.body instanceof Blob) {
        request = request.clone({
          setHeaders: {
            Accept: 'application/json, text/plain, */*',
          },
        });
      } else {
        request = this.addAuthHeader(request, data);
      }
    } else {
      if (request.body instanceof FormData) {
        request = request.clone({
          setHeaders: {
            Accept: 'application/json, text/plain, */*',
          },
        });
      } else {
        request = request.clone({
          setHeaders: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
          },
        });
      }
    }
    return request;
  }

  private addAuthHeader(request: any, data: any): any {
    if (request.body instanceof FormData) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${data}`,
          Accept: 'application/json, text/plain, */*',
        },
      });
    } else {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${data}`,
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
      });
    }
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.session.get(Storage.REFRESH_TOKEN);
      if (refreshToken) {
        return this.api.auth.refreshToken.get(refreshToken).pipe(
          switchMap((result: any) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(result.token);
            this.sessionLegacy.save(result).then();
            return next.handle(this.addHeaders(request, result.token));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.sessionLegacy.clearSession().then();
            return this.redirectToLogin().pipe(
              mergeMap(() => throwError(() => err)),
            );
          }),
        );
      } else {
        return this.redirectToLogin();
      }
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((sessionToken) => {
          return next.handle(this.addHeaders(request, sessionToken));
        }),
      );
    }
  }

  private redirectToLogin(): Observable<never> {
    this.session.remove(Storage.SESSION_TOKEN);
    this.session.remove(Storage.REFRESH_TOKEN);
    this.router.navigate(['/login']);
    return throwError(() => new Error('Unauthorized - redirected to login'));
  }
}
